import { Checkbox, Modal } from "@mui/material";
import { cloneDeep, set } from "lodash";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import ConfirmationModal from "../../../components/reusable-ui/confirmation-modal";
import CtaComponent from "../../../components/reusable-ui/cta";
import CustomDropdown from "../../../components/reusable-ui/custom-dropdown";
import { CTA_SIZE, CTA_TYPE } from "../../../data/constants";
import { isEditMode } from "../../../helpers/catalog-helper";
import { RootState } from "../../../redux/store";
import { toastSuccess } from "../../../utils/react-toaster";

interface Props {
  data?: any;
  modeConfig?: any;
  chargeId?: any;
  updateChargeDataInParentState?: any;
}

const ChannelsManagementComponent = (props: Props) => {
  const { data, updateChargeDataInParentState, modeConfig } = props;
  const allChannelsDefDataMap = useSelector(
    (state: RootState) => state.businessReducer?.allChannelsDefDataMap
  );
  const businessBranchesDataMap = useSelector(
    (state: RootState) => state.businessReducer?.businessBranchesDataMap
  );
  const [channelInfoData, setChannelInfoData] = useState<any>({});
  const [selectedChannelId, setSelectedChannelId] = useState("");
  const [openChannelInfoModal, setOpenChannelInfoModal] = useState(false);
  const [isChannelInfoEditMode, setChannelInfoEditMode] = useState(false);
  const [openChannelDeleteAlertPopup, setOpenChannelDeleteAlertPopup] =
    useState(false);
  const [errorMsgMap, setErrorMsgMap] = useState<any>({});
  const [addedChannelDefIds, setAddedChannelDefIds] = useState<any>([]);

  useEffect(() => {
    if (data?.channelInfoList?.length) {
      let dataToUpdate = data?.channelInfoList.map(
        (channelInfo: any, index: number) => {
          return channelInfo?.channelDefIds[0];
        }
      );
      setAddedChannelDefIds(dataToUpdate);
    }
  }, [data?.channelInfoList]);

  const onDeleteIconClick = (channelDefId: string) => {
    setSelectedChannelId(channelDefId);
    setOpenChannelDeleteAlertPopup(true);
  };

  const onEditIconClick = (channelInfo: any) => {
    setOpenChannelInfoModal(true);
    setChannelInfoEditMode(true);
    setChannelInfoData(cloneDeep(channelInfo));
    setSelectedChannelId(channelInfo?.channelDefIds[0]);
  };

  const onAddCtaClick = () => {
    setOpenChannelInfoModal(true);
    setChannelInfoEditMode(false);
    setChannelInfoData({});
    setSelectedChannelId("");
    setErrorMsgMap({});
  };

  const onDiscardCtaClick = () => {
    setOpenChannelInfoModal(false);
    setChannelInfoEditMode(false);
    setChannelInfoData({});
    setSelectedChannelId("");
  };

  const addChannelInfo = () => {
    let newList: any = cloneDeep(data?.channelInfoList);
    updateChargeDataInParentState("channelInfoList", [
      ...newList,
      channelInfoData,
    ]);
  };

  const editChannelInfo = () => {
    let newList: any = [];
    newList = data?.channelInfoList?.length
      ? data?.channelInfoList.map((channelInfo: any, index: number) => {
          if (
            channelInfo?.channelDefIds[0] !== channelInfoData?.channelDefIds[0]
          ) {
            return cloneDeep(channelInfo);
          } else {
            return cloneDeep(channelInfoData);
          }
        })
      : [];
    updateChargeDataInParentState("channelInfoList", newList);
  };

  const deleteChannelInfo = () => {
    let newList: any = [];
    newList = data?.channelInfoList?.length
      ? data?.channelInfoList.filter((channelInfo: any, index: number) => {
          return channelInfo?.channelDefIds[0] !== selectedChannelId;
        })
      : [];
    updateChargeDataInParentState("channelInfoList", newList);
    setOpenChannelDeleteAlertPopup(false);
    toastSuccess("Channel Deleted");
  };

  const onSaveCtaClick = () => {
    if (
      channelInfoData?.channelDefIds?.length &&
      addedChannelDefIds.indexOf(channelInfoData?.channelDefIds[0]) === -1
    ) {
      if (isChannelInfoEditMode) {
        editChannelInfo();
      } else {
        addChannelInfo();
      }
      setOpenChannelInfoModal(false);
      setChannelInfoEditMode(false);
      setChannelInfoData({});
      setSelectedChannelId("");
      return;
    }
    let errMsgMap: any = {};
    if (!channelInfoData?.channelDefIds?.length) {
      errMsgMap.channelDefIds = "This field is mandatory";
    }

    if (
      channelInfoData?.channelDefIds?.length &&
      addedChannelDefIds.indexOf(channelInfoData?.channelDefIds[0]) !== -1
    ) {
      errMsgMap.channelDefIds = "Channel already added.";
    }
    setErrorMsgMap(errMsgMap);
  };

  function renderCustomDropDown(dropdownConfig: any): React.JSX.Element {
    return (
      <CustomDropdown
        textBoxLabel={dropdownConfig.textBoxLabel}
        optionsMap={dropdownConfig.optionsList}
        isMultiSelect={dropdownConfig.isMultiSelect || false}
        placeholder={dropdownConfig.placeholder}
        getListItemText={(data: any) => {
          if (dropdownConfig.getListItemText)
            return dropdownConfig.getListItemText(data);
          else return data?.name || "";
        }}
        parentClass={dropdownConfig.parentClass}
        dropdownHeading={dropdownConfig.dropdownHeading}
        searchPlaceholder={dropdownConfig.searchPlaceholder}
        onSubmit={dropdownConfig.onSubmit}
        preSelectedValues={dropdownConfig.preSelectedValues}
        uniqueKey={dropdownConfig.uniqueKey || "mid"}
        isShowLoadMore={dropdownConfig.isShowLoadMore}
        onLoadMoreClick={dropdownConfig.onLoadMoreClick}
        onSearchApi={dropdownConfig.onSearchInput}
        isMandatory={!!dropdownConfig.isMandatory}
        errorMessage={errorMsgMap[dropdownConfig.key] || ""}
        enableSelectAll={!!dropdownConfig.enableSelectAll}
      ></CustomDropdown>
    );
  }

  const handleInputChange = (key: string, value: any) => {
    const dataToUpdate = cloneDeep(channelInfoData);
    set(dataToUpdate, key, value);
    setChannelInfoData(dataToUpdate);
  };

  const renderChannelInfoEditModal = () => {
    return (
      <Modal
        open={openChannelInfoModal}
        onClose={() => setOpenChannelInfoModal(false)}
      >
        <div className="absolute w-[636px] overflow-hidden h-[668px] rounded-[8px] left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 bg-white">
          <div className=" pt-[16px] px-[24px] pb-[36px] flex items-center justify-between">
            <div className="flex items-center">
              <div
                className="rista-sprite ic-arrow-left cursor-pointer"
                onClick={() => setOpenChannelInfoModal(false)}
              ></div>
              <h1 className="text-[22px] font-semibold">
                {isEditMode(modeConfig?.type) && selectedChannelId
                  ? `Edit - ${allChannelsDefDataMap[selectedChannelId]?.name}`
                  : "Add Channel"}
              </h1>
            </div>
            <div className="z-1 rounded-br-[8px] flex items-center">
              <CtaComponent
                ctaText={"Discard"}
                ctaType={CTA_TYPE.secondary_cta}
                ctaSize={CTA_SIZE.medium}
                onCtaClick={onDiscardCtaClick}
                customClass={"w-[96px] mr-10"}
              ></CtaComponent>
              <CtaComponent
                ctaText={"Save"}
                ctaType={CTA_TYPE.active_cta}
                ctaSize={CTA_SIZE.medium}
                onCtaClick={onSaveCtaClick}
                customClass={"w-[96px] ml-10"}
              ></CtaComponent>
            </div>
          </div>

          {renderCustomDropDown({
            textBoxLabel: "Channel",
            optionsList: allChannelsDefDataMap || {},
            placeholder: "Select Channel",
            getListItemText: (data: any) => data?.name || "",
            dropdownHeading: "Channels",
            searchPlaceholder: "Search Channel",
            parentClass: "w-full px-[24px]",
            isMultiSelect: false,
            enableSelectAll: false,
            isMandatory: true,
            preSelectedValues: channelInfoData?.channelDefIds?.length
              ? channelInfoData?.channelDefIds[0]
              : "",
            onSubmit: (value: any) => handleInputChange("channelDefIds", value),
            key: "channelDefIds",
            uniqueKey: "channelDefId",
          })}

          {renderCustomDropDown({
            textBoxLabel: "Branches",
            optionsList: businessBranchesDataMap || {},
            placeholder: "Select Branches",
            getListItemText: (data: any) => data?.branchName || "",
            dropdownHeading: "Branches",
            searchPlaceholder: "Search Branch",
            parentClass: "w-full px-[24px]",
            isMultiSelect: true,
            enableSelectAll: true,
            preSelectedValues: channelInfoData?.branchIds || [],
            onSubmit: (value: any) => handleInputChange("branchIds", value),
            key: "branchIds",
            uniqueKey: "branchId",
          })}

          {data?.applyAt === "Order" && (
            <div className="flex items-center px-[24px]">
              <Checkbox
                checked={!!channelInfoData?.isMandatory}
                onChange={(e: any) =>
                  handleInputChange("isMandatory", e.target.checked)
                }
              />
              <span className="ml-[6px] min-w-fit text-[16px]">Mandatory</span>
            </div>
          )}
        </div>
      </Modal>
    );
  };

  return (
    <section
      className={`w-[100%] ${
        isEditMode(modeConfig?.type) ? "min-h-[506px]" : ""
      }`}
    >
      {true && (
        <div
          className="border border-[#ADD3FF] bg-[#F1F7FE] text-[16px] text-[#0074FB] font-semibold flex items-center justify-center py-[9px] px-[24px] rounded-[8px] cursor-pointer mb-[20px]"
          onClick={onAddCtaClick}
        >
          + Add New Channel
        </div>
      )}

      {data?.channelInfoList?.length > 0 &&
        data?.channelInfoList?.map((channelInfo: any, index: number) => {
          return (
            <div
              key={index}
              className="border border-[#EBEBEB] rounded-[8px] mb-[16px]"
            >
              <div className="flex items-center justify-between px-[24px] py-[14px] border-b border-[#EBEBEB] bg-[#F1F7FE] rounded-t-[8px] ">
                <div className="text-[16px] font-medium">
                  {allChannelsDefDataMap[channelInfo?.channelDefIds[0]]?.name}
                </div>
                <div className="flex items-center">
                  <div
                    className="mr-[18px] rista-sprite ic-pencil-grey"
                    onClick={() => onEditIconClick(channelInfo)}
                  ></div>
                  <div
                    className="rista-sprite ic-delete-grey"
                    onClick={() =>
                      onDeleteIconClick(channelInfo?.channelDefIds[0])
                    }
                  ></div>
                </div>
              </div>

              <div className="px-[24px] py-[12px] leading-none">
                <span className="text-[13px] font-semibold">Branches: </span>
                <span className="text-[13px] font-normal">
                  {channelInfo?.branchIds?.length > 0
                    ? channelInfo?.branchIds?.map(
                        (branchId: string, index: any) => {
                          return `${index > 0 ? ", " : ""}${
                            businessBranchesDataMap[branchId]?.branchName
                          }`;
                        }
                      )
                    : businessBranchesDataMap &&
                      Object.values(businessBranchesDataMap)?.map(
                        (branchObj: any, index: number) => {
                          return `${index > 0 ? ", " : ""}${
                            branchObj?.branchName
                          }`;
                        }
                      )}
                </span>
              </div>
            </div>
          );
        })}

      {openChannelInfoModal && renderChannelInfoEditModal()}

      {openChannelDeleteAlertPopup && (
        <ConfirmationModal
          headingText="Delete Channel"
          description="This is an irreversible action. Do you still want to continue?"
          onConfirmBtnClick={deleteChannelInfo}
          onModalClose={() => setOpenChannelDeleteAlertPopup(false)}
        />
      )}
    </section>
  );
};

export default ChannelsManagementComponent;
